import { Component, Inject, Optional, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ViewContainerRef, TemplateRef, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { AuthService } from 'src/app/services/auth.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { StandingSeries } from './standing-series.component';
import flatpickr from 'flatpickr';
import moment from 'moment';

import {
  DAY_OPTIONS, DAY_MAP, REVERSE_DAY_MAP, TIME_OPTIONS,
  formatTime, formatDurationShort, formatEventDate, toLocalDateString,
  getInstructorName, calculatePreviewDates, createPopoverOverlay,
  PreviewEvent, PreviewDateConfig
} from './standing-series-utils';
import { LoggerService } from '../../../services/logger.service';

export interface StandingSeriesConflict {
  date: string;
  day: string;
  type: string;
  message: string;
  conflicting_event_id?: number;
}

export interface StandingSeriesDialogData {
  standingSeriesId: number;
  studentAccountId: string;
  entityId: string;
  initialAction?: 'view' | 'edit' | 'deleteSingle' | 'deleteSeries' | 'configure';
  conflicts?: StandingSeriesConflict[];
}

@Component({
  selector: 'app-standing-series-dialog',
  templateUrl: './standing-series-dialog.component.html',
  styleUrls: ['./standing-series-dialog.component.css']
})
export class StandingSeriesDialogComponent implements OnInit, OnDestroy {

  // --- Embedded mode inputs (used when rendered inline, not as a dialog) ---
  @Input() standingSeriesId: number | null = null;
  @Input() initialAction: string = '';
  @Input() inputConflicts: StandingSeriesConflict[] = [];
  @Input() embedded = false;
  @Output() completed = new EventEmitter<any>();

  series: StandingSeries | null = null;
  isLoading = true;
  error: string = '';

  // Edit state
  isEditing = false;
  isConfigureMode = false;
  editChanges: {
    time?: string;
    instructor?: string;
    duration?: string;
    days?: string[];
    repetitionUnits?: string;
    repetitionFrequency?: number;
    endMode?: string;
    expirationDate?: string;
    alternateInstructors?: string[];
    lessonType?: string;
    notes?: string;
    enrollmentId?: string;
    dayOverrides?: { [key: string]: { time?: string; instructor?: string; duration?: string } };
  } = {};
  editPreview: { count: number } | null = null;
  isSaving = false;
  editError: string = '';

  // Enrollment picker (edit mode)
  enrollments: any[] = [];
  enrollmentsLoading = false;
  enrollmentLessonsAvailable = 0;
  enrollmentLessonsUsed = 0;
  enrollmentLessonsTotal = 0;
  // Context header: snapshot of original series values when edit started
  originalSeries: { days: string; time: string; instructor: string; frequency: string; eventCount: number } | null = null;

  // Preview events list (base data from API or calculation)
  previewEvents: (PreviewEvent & { id?: number })[] = [];
  // Cached live preview (updated explicitly, not via getter, to avoid ngFor re-render issues)
  livePreviewEvents: (PreviewEvent & { id?: number })[] = [];
  previewLoading = false;
  private static readonly LAYOUT_KEY = 'standing_series_preview_layout';
  previewLayout: 'grid' | 'list' = 'grid';

  // Selected event for single-event editing
  selectedEvent: (PreviewEvent & { id?: number }) | null = null;
  singleEventChanges: { field_date_and_time?: string; instructor?: string; duration?: string } = {};
  isSavingSingle = false;
  // Track events that were individually edited. These keep their own values
  private individuallyEditedIds = new Set<number>();

  // Conflicts (passed from creation flow)
  conflicts: StandingSeriesConflict[] = [];
  private conflictDates = new Set<string>();
  hasConflicts = false;

  // Deactivate state
  showDeactivateConfirm = false;
  deactivateDeleteFuture = true;
  isDeactivating = false;

  // CDK Overlay for single-event popover
  @ViewChild('eventPopoverTpl') eventPopoverTpl!: TemplateRef<any>;
  private overlayRef: OverlayRef | null = null;
  showPopoverDeleteConfirm = false;

  // Delete single event state
  showDeleteSingleConfirm = false;
  isDeletingSingle = false;

  // Shared constants exposed to template
  dayOptions = DAY_OPTIONS;
  dayMap = DAY_MAP;
  timeOptions = TIME_OPTIONS;

  // CDK Overlay for rotation order popover
  @ViewChild('rotationPopoverTpl') rotationPopoverTpl!: TemplateRef<any>;
  private rotationOverlayRef: OverlayRef | null = null;

  // Flatpickr for end date
  @ViewChild('endDateInput') endDateInput!: ElementRef;
  private endDateFlatpickr?: flatpickr.Instance;
  endDateDisplayValue: string = '';

  // Popover conflict checker
  @ViewChild('popoverConflicts') popoverConflicts: any;
  editTime: string = '';
  editInstructor: string = '';
  editDateTime: string = '';

  // Skip/restore
  excludedDates = new Set<string>();
  private loadedExclusions = new Set<string>(); // exclusions loaded from entity, cleared on structural changes

  // Per-date overrides (configure mode)
  dateOverrides: { [date: string]: { time?: string; instructor?: string; duration?: string } } = {};

  // Debounced conflict checking for configure/structural mode
  private conflictCheck$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  bulkConflictDates = new Set<string>();
  private shortDateFormat = 'MM/DD/YYYY';
  private shortMonthDateFormat = 'MMM D, YYYY';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: StandingSeriesDialogData,
    @Optional() private dialogRef: MatDialogRef<StandingSeriesDialogComponent>,
    private _drupalRESTService: DrupalRESTService,
    private _entityRESTService: EntityRESTService,
    public _fieldsService: FieldsService,
    private _authService: AuthService,
    private localizationService: LocalizationService,
    private studioConfigService: StudioConfigService,
    private _snackBar: MatSnackBar,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
  ) {
    this.studioConfigService.getStudioConfig().pipe(takeUntil(this.destroy$)).subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);
      const dayFirst = formats.calendarDateFormat.startsWith('DD');
      this.shortDateFormat = dayFirst ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
      this.shortMonthDateFormat = dayFirst ? 'D MMM YYYY' : 'MMM D, YYYY';
    });
  }

  get dialogTitle(): string {
    if (this.showDeleteSingleConfirm) return 'Delete Event';
    if (this.showDeactivateConfirm) return 'Delete Series';
    if (this.isEditing && this.isConfigureMode) return 'Configure Standing Series';
    if (this.isEditing) return 'Edit Series';
    return 'Standing Series';
  }

  ngOnInit(): void {
    // Restore preview layout preference
    const savedLayout = localStorage.getItem(StandingSeriesDialogComponent.LAYOUT_KEY);
    if (savedLayout === 'list' || savedLayout === 'grid') {
      this.previewLayout = savedLayout;
    }

    // When embedded, build data from inputs
    if (this.embedded && this.standingSeriesId) {
      this.data = {
        standingSeriesId: this.standingSeriesId,
        studentAccountId: '',
        entityId: '',
        initialAction: (this.initialAction as any) || 'configure',
        conflicts: this.inputConflicts?.length ? this.inputConflicts : undefined,
      };
    }

    // Debounced conflict checking for configure/structural modes
    this.conflictCheck$.pipe(
      debounceTime(500),
      switchMap(() => this.runBulkConflictChecks()),
      takeUntil(this.destroy$)
    ).subscribe((response: any) => {
      this.bulkConflictDates.clear();
      (response.results || []).forEach((r: any) => {
        if (r.hasConflict) {
          this.bulkConflictDates.add((r.date || '').substring(0, 10));
        }
      });
    });

    if (this.data?.standingSeriesId) {
      this.loadSeries();
    }
  }

  ngOnDestroy(): void {
    this.closeEventPopover();
    this.closeRotationPopover();
    this.endDateFlatpickr?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSeries() {
    this.isLoading = true;
    this._drupalRESTService.httpGET(`/api/standing-series/${this.data.standingSeriesId}`)
      .subscribe(
        (data: any) => {
          this.isLoading = false;
          if (data?.series || data?.id) {
            this.series = data.series || data;
            this.handleInitialAction();
          } else {
            this.error = 'Standing series not found.';
          }
        },
        error => {
          this.isLoading = false;
          this.error = 'Failed to load standing series.';
          LoggerService.error('Load error:', error);
        }
      );
  }

  private handleInitialAction() {
    // Populate conflicts if passed from creation flow
    if (this.data.conflicts && this.data.conflicts.length > 0) {
      this.conflicts = this.data.conflicts;
      this.conflictDates = new Set(this.conflicts.map(c => c.date.substring(0, 10)));
      this.hasConflicts = true;
    }

    switch (this.data.initialAction) {
      case 'edit':
        this.startEdit();
        break;
      case 'deleteSingle':
        this.startDeleteSingle();
        break;
      case 'deleteSeries':
        this.startDeactivate();
        break;
      case 'configure':
        this.isConfigureMode = true;
        this.startEdit();
        break;
    }
  }

  // --- Display helpers ---

  formatDays(days: string[]): string {
    if (!days || days.length === 0) return '';
    return days.map(d => DAY_MAP[d] || d).join('/');
  }

  formatTime(time: string): string {
    return formatTime(time);
  }

  formatFrequency(series: StandingSeries): string {
    if (series.repetition_units === 'eow') return 'Every Other Week';
    if (series.repetition_units === 'monthly') return 'Monthly';
    if (series.repetition_frequency > 1) return `Every ${series.repetition_frequency} Weeks`;
    return 'Weekly';
  }

  formatEndMode(series: StandingSeries): string {
    if (series.end_mode === 'date' && series.expiration_date) {
      return 'Until ' + moment(series.expiration_date).format(this.shortMonthDateFormat);
    }
    if (series.end_mode === 'enrollment') {
      return 'Until enrollment ends';
    }
    return 'Indefinite (rolling)';
  }

  togglePreviewLayout(): void {
    this.previewLayout = this.previewLayout === 'grid' ? 'list' : 'grid';
    localStorage.setItem(StandingSeriesDialogComponent.LAYOUT_KEY, this.previewLayout);
  }

  getStatusName(tid: string): string {
    if (!tid) return '';
    const statuses = (this._fieldsService.statusTargetData() as any)?.lesson_status || [];
    const match = statuses.find((s: any) => String(s.id) === String(tid));
    return match?.name || '';
  }

  isPendingStatus(tid: string): boolean {
    const name = this.getStatusName(tid);
    return !name || name === 'Pending Status';
  }

  getStatusClass(tid: string): string {
    const name = this.getStatusName(tid);
    switch (name) {
      case 'Showed': return 'status-showed';
      case 'Cancelled': return 'status-cancelled';
      case 'Canceled Too Late': return 'status-cancelled';
      case 'Rescheduled': return 'status-rescheduled';
      case 'No Showed, Charged': return 'status-noshow';
      case 'No Showed Not Charged': return 'status-noshow';
      default: return '';
    }
  }

  getInstructorName(uid: string): string {
    return getInstructorName(uid, this._fieldsService);
  }

  formatEventDate(dateStr: string): string {
    if (!dateStr) return '';
    return moment(dateStr.substring(0, 10), 'YYYY-MM-DD', true).format(this.shortMonthDateFormat);
  }

  formatDurationShort(dur: string): string {
    return formatDurationShort(dur);
  }

  get sortedInstructors(): any[] {
    const data = this._fieldsService.instructorData();
    if (!data) return [];
    return [...data].sort((a: any, b: any) => (a.value || '').localeCompare(b.value || ''));
  }

  // --- Edit flow ---

  startEdit() {
    if (!this.series) return;
    this.isEditing = true;
    this.editChanges = {
      time: this.series.time,
      instructor: this.series.instructor_id,
      duration: this.series.duration,
      days: [...this.series.days],
      repetitionUnits: this.series.repetition_units,
      repetitionFrequency: this.series.repetition_frequency,
      endMode: this.series.end_mode,
      expirationDate: this.series.expiration_date ? this.series.expiration_date.split('T')[0] : '',
      alternateInstructors: [...(this.series.alternate_instructors || [])],
      lessonType: this.series.lesson_type || '',
      notes: this.series.notes || '',
      enrollmentId: this.series.enrollment_id || '',
      dayOverrides: {},
    };

    // Load enrollments if end mode is enrollment
    if (this.series.end_mode === 'enrollment') {
      this.loadEnrollments();
    }

    // Split stored overrides into day-name keyed (dayOverrides),
    // date-specific keyed (dateOverrides), and excluded dates.
    this.excludedDates.clear();
    this.loadedExclusions.clear();
    if (this.series.day_overrides) {
      const all = JSON.parse(JSON.stringify(this.series.day_overrides));
      const dayOvr: any = {};
      const dateOvr: any = {};
      for (const key of Object.keys(all)) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
          if (all[key].excluded) {
            this.excludedDates.add(key);
            this.loadedExclusions.add(key);
          } else {
            dateOvr[key] = all[key];
          }
        } else {
          dayOvr[key] = all[key];
        }
      }
      this.editChanges.dayOverrides = dayOvr;
      this.dateOverrides = dateOvr;
    } else {
      this.dateOverrides = {};
    }

    this.editPreview = null;
    this.editError = '';
    this.selectedEvent = null;
    this.singleEventChanges = {};
    this.individuallyEditedIds.clear();

    // Init flatpickr display value
    if (this.editChanges.expirationDate) {
      const d = new Date(this.editChanges.expirationDate + 'T12:00:00');
      this.endDateDisplayValue = this.formatEndDateDisplay(d);
    } else {
      this.endDateDisplayValue = '';
    }

    // Snapshot original series for context header
    this.originalSeries = {
      days: this.formatDays(this.series.days),
      time: this.formatTime(this.series.time),
      instructor: this.getInstructorName(this.series.instructor_id),
      frequency: this.formatFrequency(this.series),
      eventCount: (this.series.events || []).length,
    };

    // Auto-load preview
    this.loadPreview();
  }

  cancelEdit() {
    this.closeEventPopover();
    this.closeRotationPopover();

    // In configure mode, deactivate the orphaned entity (no events were generated)
    if (this.isConfigureMode && this.series) {
      this._drupalRESTService.httpPOST(
        `/api/standing-series/${this.series.id}/deactivate`,
        { delete_future: false }
      ).subscribe(() => {}, () => {});
    }

    this.resetEditState();
  }

  /** Close the dialog after a successful action so the caller refreshes the calendar. */
  private finishAndClose(result: any) {
    this.closeEventPopover();
    this.closeRotationPopover();
    this.resetEditState();
    this.closeWith(result);
  }

  private resetEditState() {
    this.isEditing = false;
    this.isConfigureMode = false;
    this.editPreview = null;
    this.editError = '';
    this.originalSeries = null;
    this.previewEvents = [];
    this.livePreviewEvents = [];
    this.selectedEvent = null;
    this.singleEventChanges = {};
    this.excludedDates.clear();
    this.loadedExclusions.clear();
    this.dateOverrides = {};
    this.bulkConflictDates.clear();
    this.conflicts = [];
    this.conflictDates.clear();
    this.hasConflicts = false;
  }

  // Day toggle helpers
  toggleDay(day: string) {
    if (!this.editChanges.days) this.editChanges.days = [];
    const idx = this.editChanges.days.indexOf(day);
    if (idx > -1) {
      this.editChanges.days.splice(idx, 1);
      if (this.editChanges.dayOverrides) {
        delete this.editChanges.dayOverrides[day];
      }
    } else {
      this.editChanges.days.push(day);
    }
    this.updateLivePreview();
  }

  isDaySelected(day: string): boolean {
    return this.editChanges.days?.includes(day) || false;
  }

  /** Check if a given day has a day override */
  hasDayOverride(dayKey: string): boolean {
    const ovr = this.editChanges.dayOverrides?.[dayKey];
    return !!(ovr && (ovr.time || ovr.instructor || ovr.duration));
  }

  /** Check if a date has a per-date override (configure mode) */
  hasDateOverride(date: string): boolean {
    return !!this.dateOverrides[date];
  }

  // Alternate instructor helpers
  onAlternatesChanged() {
    // Sort alternates to match the alphabetical display order so the rotation
    // order the user sees in the dropdown matches what gets saved/generated.
    if (this.editChanges.alternateInstructors) {
      const nameMap = new Map<string, string>();
      for (const inst of (this._fieldsService.instructorData() || [])) {
        nameMap.set(String(inst.uid), (inst.value || '').toLowerCase());
      }
      this.editChanges.alternateInstructors.sort((a, b) =>
        (nameMap.get(a) || '').localeCompare(nameMap.get(b) || '')
      );
    }
    this.updateLivePreview();
  }

  onRotationReorder(event: CdkDragDrop<string[]>) {
    if (!this.editChanges.alternateInstructors) return;
    moveItemInArray(this.editChanges.alternateInstructors, event.previousIndex, event.currentIndex);
    this.updateLivePreview();
  }

  removeAlternate(index: number) {
    if (!this.editChanges.alternateInstructors) return;
    this.editChanges.alternateInstructors.splice(index, 1);
    this.updateLivePreview();
  }

  // --- Rotation popover ---

  openRotationPopover(origin: HTMLElement): void {
    if (this.rotationOverlayRef) {
      this.closeRotationPopover();
      return;
    }
    this.rotationOverlayRef = createPopoverOverlay(
      this.overlay, origin, this.rotationPopoverTpl, this.viewContainerRef,
      { width: 300, onClose: () => this.closeRotationPopover() }
    );
  }

  closeRotationPopover(): void {
    if (this.rotationOverlayRef) {
      this.rotationOverlayRef.dispose();
      this.rotationOverlayRef = null;
    }
  }

  // --- Flatpickr for end date ---

  initEndDateFlatpickr(): void {
    if (this.endDateFlatpickr || !this.endDateInput) return;

    this.endDateFlatpickr = flatpickr(this.endDateInput.nativeElement, {
      dateFormat: 'm/d/Y',
      disableMobile: true,
      defaultDate: this.editChanges.expirationDate ? new Date(this.editChanges.expirationDate + 'T12:00:00') : undefined,
      onChange: (selectedDates) => {
        if (selectedDates.length > 0) {
          this.editChanges.expirationDate = toLocalDateString(selectedDates[0]);
          this.endDateDisplayValue = this.formatEndDateDisplay(selectedDates[0]);
          this.updateLivePreview();
        }
      },
    });
  }

  openEndDatePicker(): void {
    if (!this.endDateFlatpickr) {
      this.initEndDateFlatpickr();
    }
    this.endDateFlatpickr?.open();
  }

  private formatEndDateDisplay(d: Date): string {
    return moment(d).format(this.shortDateFormat);
  }

  // --- Enrollment picker ---

  onEndModeChange(): void {
    if (this.editChanges.endMode === 'enrollment' && this.enrollments.length === 0) {
      this.loadEnrollments();
    }
    this.updateLivePreview();
  }

  loadEnrollments(): void {
    if (!this.series?.student_account_id) return;
    this.enrollmentsLoading = true;
    this._drupalRESTService.httpGET('/api_rest/v1/enrollmentLookupDetailed', [
      { parameter: 'u', value: this.series.student_account_id },
    ]).subscribe(
      (data: any) => {
        this.enrollmentsLoading = false;
        this.enrollments = Array.isArray(data) ? data : [];
        // If an enrollment is already selected, compute its lesson counts
        if (this.editChanges.enrollmentId) {
          this.updateEnrollmentCounts(this.editChanges.enrollmentId);
        }
      },
      () => {
        this.enrollmentsLoading = false;
        this.enrollments = [];
      }
    );
  }

  onEnrollmentChange(): void {
    if (this.editChanges.enrollmentId) {
      this.updateEnrollmentCounts(this.editChanges.enrollmentId);
    } else {
      this.enrollmentLessonsAvailable = 0;
      this.enrollmentLessonsUsed = 0;
      this.enrollmentLessonsTotal = 0;
    }
    // Update the series object so buildPreviewDates and max_events use the new value
    if (this.series) {
      this.series.enrollment_lessons_available = this.enrollmentLessonsAvailable;
    }
    this.updateLivePreview();
  }

  private updateEnrollmentCounts(enrollmentId: string): void {
    const enrollment = this.enrollments.find(
      (e: any) => String(e.id?.[0]?.value) === String(enrollmentId)
    );
    if (enrollment) {
      this.enrollmentLessonsTotal = Number(enrollment.field_enrollment_lesson_count?.[0]?.value || 0);
      this.enrollmentLessonsUsed = Number(enrollment.field_lesson_used?.[0]?.value || 0);
      this.enrollmentLessonsAvailable = Math.max(0, this.enrollmentLessonsTotal - this.enrollmentLessonsUsed);
    } else {
      this.enrollmentLessonsAvailable = this.series?.enrollment_lessons_available || 0;
      this.enrollmentLessonsTotal = 0;
      this.enrollmentLessonsUsed = 0;
    }
  }

  // Detect if changes require regeneration
  hasStructuralChanges(): boolean {
    if (!this.series) return false;
    const c = this.editChanges;
    const s = this.series;
    return (
      JSON.stringify([...(c.days || [])].sort()) !== JSON.stringify([...s.days].sort()) ||
      c.repetitionUnits !== s.repetition_units ||
      Number(c.repetitionFrequency) !== Number(s.repetition_frequency) ||
      c.endMode !== s.end_mode ||
      c.expirationDate !== (s.expiration_date?.split('T')[0] || '') ||
      (c.enrollmentId || '') !== (s.enrollment_id || '') ||
      JSON.stringify(c.alternateInstructors || []) !== JSON.stringify(s.alternate_instructors || []) ||
      JSON.stringify(c.dayOverrides || {}) !== JSON.stringify(this.getSeriesDayNameOverrides())
    );
  }

  /** Extract only day-name keyed overrides from the loaded series (excluding date-specific keys). */
  private getSeriesDayNameOverrides(): any {
    if (!this.series?.day_overrides) return {};
    const result: any = {};
    for (const key of Object.keys(this.series.day_overrides)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        result[key] = this.series.day_overrides[key];
      }
    }
    return result;
  }

  loadPreview() {
    if (!this.series) return;

    if (this.isConfigureMode || this.hasStructuralChanges()) {
      this.previewEvents = [...this.getPastSeriesEvents(), ...this.buildPreviewDates()];
      this.editPreview = { count: this.previewEvents.length };
      this.updateLivePreview();
      return;
    }

    // Use events already loaded from the series GET response.
    const eventDates = new Set<string>();
    this.previewEvents = (this.series.events || []).map((evt: any) => {
      const dateKey = (evt.date || '').substring(0, 10);
      eventDates.add(dateKey);
      return {
        id: evt.id,
        date: evt.date,
        day: DAY_MAP[evt.day_of_week] || evt.day_of_week,
        dayKey: evt.day_of_week || REVERSE_DAY_MAP[DAY_MAP[evt.day_of_week]] || '',
        time: formatTime(evt.date?.substring(11, 16) || ''),
        instructor: getInstructorName(String(evt.instructor_id || ''), this._fieldsService),
        instructorId: String(evt.instructor_id || ''),
        duration: formatDurationShort(evt.duration || ''),
        statusTid: evt.status_tid ? String(evt.status_tid) : undefined,
      };
    });

    // Add placeholder entries for excluded (deleted) dates so they show as skipped.
    // Only show placeholders for dates that match the current series days.
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const seriesDays = new Set(this.series.days || []);
    const staleExcluded: string[] = [];
    for (const dateStr of this.excludedDates) {
      const dateKey = dateStr.substring(0, 10);
      if (eventDates.has(dateKey)) continue;
      const d = new Date(dateKey + 'T12:00:00');
      const dayKey = dayNames[d.getDay()] || '';
      if (!seriesDays.has(dayKey)) {
        // This excluded date is for a day no longer in the series. Clean it up
        staleExcluded.push(dateStr);
        continue;
      }
      this.previewEvents.push({
        date: dateKey + 'T' + (this.series.time || '12:00') + ':00',
        day: DAY_MAP[dayKey] || dayKey,
        dayKey,
        time: formatTime(this.series.time || ''),
        instructor: '',
        instructorId: '',
        duration: formatDurationShort(this.series.duration || ''),
      });
    }
    // Remove stale exclusions for days no longer in the series
    for (const stale of staleExcluded) {
      this.excludedDates.delete(stale);
    }

    // Sort by date so excluded placeholders appear in the right position
    this.previewEvents.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

    this.editPreview = { count: this.previewEvents.length };
    this.updateLivePreview();
  }

  /** Build preview dates using the unified calculation from utils */
  private buildPreviewDates(): PreviewEvent[] {
    const c = this.editChanges;
    const config: PreviewDateConfig = {
      days: c.days || [],
      time: c.time || '',
      duration: c.duration || '',
      instructorId: c.instructor || '',
      alternateInstructors: c.alternateInstructors || [],
      repetitionUnits: c.repetitionUnits || 'weekly',
      repetitionFrequency: c.repetitionFrequency || 1,
      endMode: c.endMode || 'indefinite',
      expirationDate: c.expirationDate || '',
      startDate: this.series?.start_date || '',
      enrollmentLessonsAvailable: this.series?.enrollment_lessons_available || 0,
      dateOverrides: this.dateOverrides,
      dayOverrides: c.dayOverrides,
      fieldsService: this._fieldsService,
    };
    return calculatePreviewDates(config);
  }

  /** Return past events from the loaded series (already posted, won't be deleted). */
  private getPastSeriesEvents(): (PreviewEvent & { id?: number })[] {
    if (this.isConfigureMode || !this.series?.events) return [];
    const today = new Date().toISOString().substring(0, 10);
    return this.series.events
      .filter((evt: any) => (evt.date || '').substring(0, 10) < today)
      .map((evt: any) => ({
        id: evt.id,
        date: evt.date,
        day: DAY_MAP[evt.day_of_week] || evt.day_of_week,
        dayKey: evt.day_of_week || REVERSE_DAY_MAP[DAY_MAP[evt.day_of_week]] || '',
        time: formatTime(evt.date?.substring(11, 16) || ''),
        instructor: getInstructorName(String(evt.instructor_id || ''), this._fieldsService),
        instructorId: String(evt.instructor_id || ''),
        duration: formatDurationShort(evt.duration || ''),
        statusTid: evt.status_tid ? String(evt.status_tid) : undefined,
      }));
  }

  getChangeSummary(): string[] {
    if (!this.series) return [];
    const changes: string[] = [];
    const c = this.editChanges;
    const s = this.series;
    if (c.time !== s.time) {
      changes.push(`Time: ${formatTime(s.time)} → ${formatTime(c.time || '')}`);
    }
    if (c.instructor !== s.instructor_id) {
      changes.push(`Instructor: ${this.getInstructorName(s.instructor_id)} → ${this.getInstructorName(c.instructor || '')}`);
    }
    if (c.duration !== s.duration) {
      changes.push(`Duration: ${formatDurationShort(s.duration)}m → ${formatDurationShort(c.duration || '')}m`);
    }
    return changes;
  }

  /**
   * Rebuild the cached livePreviewEvents array.
   * Call this whenever editChanges, previewEvents, or structural inputs change.
   */
  updateLivePreview(): void {
    if (this.isConfigureMode || this.hasStructuralChanges()) {
      // Clear stale exclusions loaded from the entity. They no longer apply
      // after a structural change. Keep user-added exclusions from this session.
      for (const date of this.loadedExclusions) {
        this.excludedDates.delete(date);
      }
      this.loadedExclusions.clear();
      this.livePreviewEvents = [...this.getPastSeriesEvents(), ...this.buildPreviewDates()];
      this.conflictCheck$.next();
      return;
    }

    const c = this.editChanges;
    const s = this.series;
    const timeChanged = c.time !== s?.time;
    const instructorChanged = c.instructor !== s?.instructor_id;
    const durationChanged = c.duration !== s?.duration;
    const hasSimpleChanges = timeChanged || instructorChanged || durationChanged;

    // If nothing changed at the series level, events from the API already have
    // the correct time/instructor/duration (including rotation). Pass them through.
    // Individual edits were already applied directly to previewEvents by saveSingleEvent().
    if (!hasSimpleChanges) {
      this.livePreviewEvents = [...this.previewEvents];
      this.conflictCheck$.next();
      return;
    }

    const useRotation = !!(c.alternateInstructors && c.alternateInstructors.length > 0);
    const instructorPool: string[] = [c.instructor || ''];
    if (useRotation) {
      instructorPool.push(...c.alternateInstructors!);
    }
    let rotationIndex = 0;

    this.livePreviewEvents = this.previewEvents.map(evt => {
      // Individually-edited events keep their saved values
      if (evt.id && this.individuallyEditedIds.has(evt.id)) {
        if (useRotation) rotationIndex++;
        return evt;
      }

      const dayKey = REVERSE_DAY_MAP[evt.day] || evt.day.toLowerCase();
      const dateStr = evt.date?.substring(0, 10) || evt.date;
      const dateOvr = this.dateOverrides[dateStr];
      const dayOvr = c.dayOverrides?.[dayKey];

      let instructor: string;
      let instructorId: string;
      if (dateOvr?.instructor) {
        instructorId = dateOvr.instructor;
        instructor = this.getInstructorName(dateOvr.instructor);
        // Override still consumes a rotation slot so subsequent events stay aligned
        if (useRotation) rotationIndex++;
      } else if (dayOvr?.instructor) {
        instructorId = dayOvr.instructor;
        instructor = this.getInstructorName(dayOvr.instructor);
        if (useRotation) rotationIndex++;
      } else if (useRotation) {
        instructorId = instructorPool[rotationIndex % instructorPool.length];
        instructor = this.getInstructorName(instructorId);
        rotationIndex++;
      } else {
        instructorId = c.instructor || '';
        instructor = this.getInstructorName(c.instructor || '');
      }

      return {
        ...evt,
        time: formatTime(dateOvr?.time || dayOvr?.time || c.time || ''),
        instructor,
        instructorId,
        duration: formatDurationShort(dateOvr?.duration || dayOvr?.duration || c.duration || ''),
      };
    });

    // Non-structural changes (time, instructor, duration) still need conflict checks.
    this.conflictCheck$.next();
  }

  trackEvent(index: number, evt: { id?: number; date: string; day: string }): string {
    return evt.id ? String(evt.id) : `${evt.date}-${evt.day}`;
  }

  // --- Skip/restore ---

  isEventExcluded(evt: { date: string }): boolean {
    return this.excludedDates.has(evt.date.substring(0, 10));
  }

  toggleExclude(evt: { date: string }, event: MouseEvent): void {
    event.stopPropagation();
    const dateKey = evt.date.substring(0, 10);
    if (this.excludedDates.has(dateKey)) {
      this.excludedDates.delete(dateKey);
    } else {
      this.excludedDates.add(dateKey);
      if (this.selectedEvent?.date === evt.date) {
        this.selectedEvent = null;
      }
    }
  }

  get activePreviewCount(): number {
    return this.livePreviewEvents.filter(e => !this.excludedDates.has(e.date.substring(0, 10))).length;
  }

  // --- Single event selection and editing ---

  isEventSelected(evt: { id?: number; date: string; day: string }): boolean {
    if (!this.selectedEvent) return false;
    return this.selectedEvent.id === evt.id && this.selectedEvent.date === evt.date;
  }

  isEventConflict(evt: { date: string }): boolean {
    const dateStr = evt.date.substring(0, 10);
    if (this.hasConflicts && this.conflictDates.has(dateStr)) return true;
    if (this.bulkConflictDates.has(dateStr)) return true;
    return false;
  }

  getEventConflict(evt: { date: string }): StandingSeriesConflict | undefined {
    if (!this.hasConflicts) return undefined;
    const dateStr = evt.date.substring(0, 10);
    return this.conflicts.find(c => c.date.substring(0, 10) === dateStr);
  }

  selectEvent(evt: PreviewEvent & { id?: number }, cardElement?: HTMLElement) {
    if (this.isEventPast(evt.date)) return;
    if (this.isEventPosted(evt)) return;
    if (this.isEventExcluded(evt)) return;

    // If already selected, deselect
    if (this.selectedEvent && this.selectedEvent.id === evt.id && this.selectedEvent.date === evt.date) {
      this.cancelSingleEvent();
      return;
    }

    this.selectedEvent = { ...evt };
    const dayKey = REVERSE_DAY_MAP[evt.day] || evt.day.toLowerCase();
    const dayOverride = this.editChanges.dayOverrides?.[dayKey];
    const dateOverride = this.dateOverrides[evt.date];
    const time = dateOverride?.time || dayOverride?.time || this.editChanges.time || this.series?.time || '';

    // Build full datetime from the event's date + time
    const datePart = evt.date.substring(0, 10);
    const dateTime = datePart + 'T' + (time.length === 5 ? time + ':00' : time);

    // Use the event's actual instructor (from rotation) rather than the
    // primary instructor fallback, so the popover shows the correct teacher.
    const evtInstructor = evt.instructorId || dateOverride?.instructor || dayOverride?.instructor || this.editChanges.instructor || this.series?.instructor_id || '';

    this.singleEventChanges = {
      field_date_and_time: dateTime,
      instructor: evtInstructor,
      duration: dayOverride?.duration || this.editChanges.duration || this.series?.duration || '',
    };

    this.editTime = time.substring(0, 5);
    this.editInstructor = evtInstructor;
    this.updateEditDateTime();

    // If the base event has its own specific values (from a prior single-event edit), use those
    if (evt.id) {
      const baseEvt = this.previewEvents.find(e => e.id === evt.id);
      if (baseEvt) {
        const rawDate = baseEvt.date;
        if (rawDate && rawDate.includes('T')) {
          this.singleEventChanges.field_date_and_time = rawDate;
        }
      }
    }

    // Open popover anchored to the clicked card
    if (cardElement) {
      this.openEventPopover(cardElement);
      setTimeout(() => this.popoverConflicts?.checkScheduleConflicts(), 0);
    }
  }

  cancelSingleEvent() {
    this.closeEventPopover();
    this.showPopoverDeleteConfirm = false;
    this.selectedEvent = null;
    this.singleEventChanges = {};
  }

  // --- Popover conflict helpers ---

  onEditTimeChange(): void {
    // Update singleEventChanges datetime from editTime
    if (this.selectedEvent) {
      const datePart = this.selectedEvent.date.substring(0, 10);
      this.singleEventChanges.field_date_and_time = datePart + 'T' + (this.editTime.length === 5 ? this.editTime + ':00' : this.editTime);
    }
    this.updateEditDateTime();
    this.popoverConflicts?.checkScheduleConflicts();
  }

  onEditInstructorChange(): void {
    this.singleEventChanges.instructor = this.editInstructor;
    this.popoverConflicts?.checkScheduleConflicts();
  }

  onPopoverAlternativeTime(newDateTime: string): void {
    if (!newDateTime || !this.selectedEvent) return;
    const timePart = newDateTime.substring(11, 16);
    this.editTime = timePart;
    this.singleEventChanges.field_date_and_time = newDateTime;
    this.updateEditDateTime();
    this.popoverConflicts?.checkScheduleConflicts();
  }

  private updateEditDateTime(): void {
    if (this.selectedEvent && this.editTime) {
      this.editDateTime = `${this.selectedEvent.date.substring(0, 10)}T${this.editTime}:00`;
    }
  }

  private openEventPopover(origin: HTMLElement) {
    this.closeEventPopover();
    this.overlayRef = createPopoverOverlay(
      this.overlay, origin, this.eventPopoverTpl, this.viewContainerRef,
      { width: 320, onClose: () => this.cancelSingleEvent() }
    );
  }

  private closeEventPopover() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  startDeleteSingleFromPopover() {
    this.showPopoverDeleteConfirm = true;
  }

  cancelDeleteSingleFromPopover() {
    this.showPopoverDeleteConfirm = false;
  }

  confirmDeleteSingleFromPopover() {
    if (!this.series || !this.selectedEvent?.id) return;
    this.isDeletingSingle = true;

    this._drupalRESTService.httpPOST(
      `/api/standing-series/${this.series.id}/delete-future`,
      {
        scope: 'single',
        event_id: this.selectedEvent.id,
        confirm: true
      }
    ).subscribe(
      (data: any) => {
        this.isDeletingSingle = false;
        if (data?.success) {
          this._snackBar.open('Event deleted.', 'OK', { duration: 3000 });
          const deletedId = this.selectedEvent!.id!;
          this.previewEvents = this.previewEvents.filter(e => e.id !== deletedId);
          this.livePreviewEvents = this.livePreviewEvents.filter(e => e.id !== deletedId);
          this.showPopoverDeleteConfirm = false;
          this.cancelSingleEvent();
        } else {
          this.editError = data?.message || 'Failed to delete event.';
          this.showPopoverDeleteConfirm = false;
          this.cancelSingleEvent();
        }
      },
      error => {
        this.isDeletingSingle = false;
        this.editError = 'Failed to delete event.';
        this.showPopoverDeleteConfirm = false;
        this.cancelSingleEvent();
        LoggerService.error('Delete single error:', error);
      }
    );
  }

  saveSingleEvent() {
    if (!this.series || !this.selectedEvent) return;

    // For events with an ID (existing events), use the backend API
    if (this.selectedEvent.id) {
      this.isSavingSingle = true;
      const dtVal = this.singleEventChanges.field_date_and_time || '';
      const timeFromDt = dtVal.includes('T') ? dtVal.substring(11, 16) : dtVal;

      const body = {
        scope: 'single',
        event_id: this.selectedEvent.id,
        changes: {
          field_date_and_time: dtVal,
          time: timeFromDt,
          field_instructor: this.singleEventChanges.instructor,
          field_duration: this.singleEventChanges.duration,
        },
        confirm: true,
      };

      this._drupalRESTService.httpPOST(
        `/api/standing-series/${this.series.id}/edit-future`,
        body
      ).subscribe(
        (data: any) => {
          this.isSavingSingle = false;
          if (data?.success) {
            this._snackBar.open('Event updated.', 'OK', { duration: 3000 });
            const evtId = this.selectedEvent!.id!;
            this.individuallyEditedIds.add(evtId);
            const idx = this.previewEvents.findIndex(e => e.id === evtId);
            if (idx > -1) {
              this.previewEvents[idx] = {
                ...this.previewEvents[idx],
                date: dtVal || this.previewEvents[idx].date,
                time: formatTime(timeFromDt || ''),
                instructor: this.getInstructorName(this.singleEventChanges.instructor || ''),
                instructorId: this.singleEventChanges.instructor || '',
                duration: formatDurationShort(this.singleEventChanges.duration || ''),
              };
            }
            this.cancelSingleEvent();
            this.updateLivePreview();
          } else {
            this.editError = data?.message || 'Failed to update event.';
          }
        },
        error => {
          this.isSavingSingle = false;
          this.editError = 'Failed to update event.';
        }
      );
    } else {
      // For calculated (structural/configure) preview events without IDs,
      // apply as a dateOverride and rebuild preview
      const dateKey = this.selectedEvent.date.substring(0, 10);
      const defaultTime = this.editChanges.time || this.series?.time || '';
      // Compare against the event's rotation-assigned instructor (not the primary)
      // so saving an event with its already-assigned teacher doesn't create a spurious override.
      const evtDefaultInstructor = this.selectedEvent.instructorId || this.editChanges.instructor || this.series?.instructor_id || '';
      const hasTimeOverride = this.editTime && this.editTime !== defaultTime;
      const hasInstructorOverride = this.editInstructor && this.editInstructor !== evtDefaultInstructor;

      if (hasTimeOverride || hasInstructorOverride) {
        this.dateOverrides[dateKey] = {};
        if (hasTimeOverride) this.dateOverrides[dateKey].time = this.editTime;
        if (hasInstructorOverride) this.dateOverrides[dateKey].instructor = this.editInstructor;
      } else {
        delete this.dateOverrides[dateKey];
      }

      this.cancelSingleEvent();
      this.updateLivePreview();
    }
  }

  isEventPast(dateStr: string): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr.substring(0, 10) + 'T23:59:59');
    return d < new Date();
  }

  /** Event has been posted (non-Pending status); should not be editable */
  isEventPosted(evt: PreviewEvent): boolean {
    return !!(evt.statusTid && !this.isPendingStatus(evt.statusTid));
  }

  dismissConflicts() {
    this.conflicts = [];
    this.conflictDates.clear();
    this.hasConflicts = false;
  }

  // --- Bulk conflict checking for configure/structural modes ---

  private runBulkConflictChecks() {
    const activeEvents = this.livePreviewEvents.filter(e => !this.excludedDates.has(e.date.substring(0, 10)));
    if (activeEvents.length === 0) return of({ results: [] });

    const studioId = this._authService?.selectedStudio?.id || '1';
    const c = this.editChanges;

    const events = activeEvents.map(evt => {
      const dateOvr = this.dateOverrides[evt.date];
      const dayKey = REVERSE_DAY_MAP[evt.day] || evt.dayKey || evt.day.toLowerCase();
      const dayOvr = c.dayOverrides?.[dayKey];
      const evtTime = dateOvr?.time || dayOvr?.time || c.time || this.series?.time || '12:00';
      return {
        date: evt.date,
        field_date_and_time: `${evt.date.substring(0, 10)}T${evtTime}:00`,
        field_duration: c.duration || this.series?.duration || '00:45',
        field_instructor: evt.instructorId || c.instructor || this.series?.instructor_id || '',
        field_studio_reference: studioId,
        exclude_event_id: evt.id ? String(evt.id) : null,
      };
    });

    const payload = {
      events,
      exclude_standing_series_id: this.series?.id ? String(this.series.id) : null,
    };
    LoggerService.log('[CONFLICT CHECK] Sending batch:', events.map(e => ({
      date: e.date, instructor: e.field_instructor, time: e.field_date_and_time
    })));
    return this._drupalRESTService.httpPOST('/api_rest/v1/checkScheduleConflictsBatch', payload).pipe(
      tap((response: any) => {
        const conflicts = (response.results || []).filter((r: any) => r.hasConflict);
        if (conflicts.length > 0) {
          const conflictDetails = conflicts.map((r: any) => {
            const matchEvt = events.find(e => (e.date || '').substring(0, 10) === (r.date || '').substring(0, 10));
            return { date: r.date, instructor: matchEvt?.field_instructor };
          });
          LoggerService.log('[CONFLICT CHECK] Conflicts found:', conflictDetails);
        }
      }),
      catchError(() => of({ results: [] }))
    );
  }

  saveEdit() {
    if (!this.series) return;

    if (this.hasStructuralChanges()) {
      this.saveStructuralEdit();
    } else {
      this.saveSimpleEdit();
    }
  }

  private saveSimpleEdit() {
    if (!this.series) return;
    this.isSaving = true;
    this.editError = '';

    // Build changes object with only fields that actually changed at the series level
    const changes: any = {};
    if (this.editChanges.time !== this.series.time) {
      changes.time = this.editChanges.time;
    }
    if (this.editChanges.instructor !== this.series.instructor_id) {
      changes.field_instructor = this.editChanges.instructor;
    }
    if (this.editChanges.duration !== this.series.duration) {
      changes.field_duration = this.editChanges.duration;
    }

    const hasSeriesChanges = Object.keys(changes).length > 0;
    const notesChanged = (this.editChanges.notes || '') !== (this.series.notes || '');

    // Persist excluded dates (skips), date overrides, and notes on the series entity
    const persistSeriesPatch = () => {
      const patch: any = {};
      const allOverrides: any = { ...(this.editChanges.dayOverrides || {}), ...this.dateOverrides };
      // Add excluded dates as {"excluded": true} entries
      for (const date of this.excludedDates) {
        const dateKey = date.substring(0, 10);
        allOverrides[dateKey] = { ...(allOverrides[dateKey] || {}), excluded: true };
      }
      if (Object.keys(allOverrides).length > 0 ||
          (this.series!.day_overrides && Object.keys(this.series!.day_overrides).length > 0)) {
        patch.field_day_overrides = Object.keys(allOverrides).length > 0
          ? [{ value: JSON.stringify(allOverrides) }]
          : [];
      }
      if (notesChanged) {
        patch.field_notes = this.editChanges.notes ? [{ value: this.editChanges.notes }] : [];
      }
      if (Object.keys(patch).length > 0) {
        return this._entityRESTService.patchEntity('standing_series', 'standing', this.series!.id, patch);
      }
      return null;
    };

    // Find excluded events that have IDs (existing events to delete)
    const excludedEventIds = this.livePreviewEvents
      .filter(e => e.id && this.excludedDates.has(e.date.substring(0, 10)))
      .map(e => e.id!);

    const doDeleteExcluded = (then: () => void) => {
      if (excludedEventIds.length === 0) {
        then();
        return;
      }
      let remaining = excludedEventIds.length;
      excludedEventIds.forEach(eventId => {
        this._drupalRESTService.httpPOST(
          `/api/standing-series/${this.series!.id}/delete-future`,
          { scope: 'single', event_id: eventId, confirm: true }
        ).subscribe(
          () => { if (--remaining === 0) then(); },
          () => { if (--remaining === 0) then(); }
        );
      });
    };

    const doEditFuture = () => {
      if (!hasSeriesChanges) {
        // No series-level changes, just finish up
        this.isSaving = false;
        const deletedCount = excludedEventIds.length;
        const editedCount = this.individuallyEditedIds.size;
        const parts: string[] = [];
        if (editedCount > 0) parts.push(`${editedCount} event${editedCount !== 1 ? 's' : ''} edited`);
        if (deletedCount > 0) parts.push(`${deletedCount} event${deletedCount !== 1 ? 's' : ''} skipped`);
        if (notesChanged) parts.push('notes updated');
        const anythingChanged = parts.length > 0;
        this._snackBar.open(
          anythingChanged ? `Saved: ${parts.join(', ')}.` : 'No changes to save.',
          'OK',
          { duration: 5000 }
        );
        this.finishAndClose(anythingChanged ? { action: 'edited' } : undefined);
        return;
      }

      const body: any = {
        scope: 'all',
        from_date: toLocalDateString(new Date()),
        changes,
        confirm: true,
      };

      // Exclude individually-edited events so the bulk update doesn't overwrite them
      const allExcludeIds = [...new Set([
        ...this.individuallyEditedIds,
        ...excludedEventIds,
      ])];
      if (allExcludeIds.length > 0) {
        body.exclude_event_ids = allExcludeIds;
      }

      this._drupalRESTService.httpPOST(
        `/api/standing-series/${this.series!.id}/edit-future`,
        body
      ).subscribe(
        (data: any) => {
          this.isSaving = false;
          if (data?.success) {
            this._snackBar.open(
              `Updated ${data.updated_count || 0} future events.`,
              'OK',
              { duration: 5000 }
            );
            this.finishAndClose({ action: 'edited', updatedCount: data.updated_count || 0 });
          } else {
            this.editError = data?.message || 'Failed to save changes.';
          }
        },
        error => {
          this.isSaving = false;
          this.editError = 'Failed to save changes.';
        }
      );
    };

    // Chain: persist overrides → delete excluded events → bulk edit remaining
    const overridePatch$ = persistSeriesPatch();
    if (overridePatch$) {
      overridePatch$.subscribe(
        () => doDeleteExcluded(() => doEditFuture()),
        () => doDeleteExcluded(() => doEditFuture())
      );
    } else {
      doDeleteExcluded(() => doEditFuture());
    }
  }

  private saveStructuralEdit() {
    if (!this.series) return;
    this.isSaving = true;
    this.editError = '';

    const c = this.editChanges;
    const patchData = this.buildPatchData(c);

    // Step 1: PATCH the series entity
    this._entityRESTService.patchEntity('standing_series', 'standing', this.series.id, patchData)
      .subscribe(
        () => {
          // Step 2: Delete future unposted events
          this._drupalRESTService.httpPOST(
            `/api/standing-series/${this.series!.id}/delete-future`,
            { scope: 'all', from_date: toLocalDateString(new Date()), confirm: true }
          ).subscribe(
            () => {
              // Step 3: Regenerate events
              const generateBody: any = {
                check_conflicts: false,
                clear_exclusions: true,
                excluded_dates: [...this.excludedDates],
                attendee_data: {
                  field_enrollment: this.editChanges.enrollmentId || this.series!.enrollment_id || '',
                  field_studio_reference: this._authService?.selectedStudio?.id || '',
                },
              };
              if (this.editChanges.endMode === 'enrollment' && this.series!.enrollment_lessons_available > 0) {
                generateBody.max_events = this.series!.enrollment_lessons_available;
              }
              this._drupalRESTService.httpPOST(
                `/api/standing-series/${this.series!.id}/generate`,
                generateBody
              ).subscribe(
                (genData: any) => {
                  this.isSaving = false;
                  this._snackBar.open(
                    `Series updated and ${genData?.created_count || 0} events regenerated.`,
                    'OK',
                    { duration: 5000 }
                  );
                  this.finishAndClose({ action: 'regenerated', createdCount: genData?.created_count || 0 });
                },
                error => {
                  this.isSaving = false;
                  this.editError = 'Series updated but failed to regenerate events. Try opening the series again.';
                }
              );
            },
            error => {
              this.isSaving = false;
              this.editError = 'Series updated but failed to clean up old events.';
            }
          );
        },
        error => {
          this.isSaving = false;
          this.editError = 'Failed to update series.';
          LoggerService.error('Patch error:', error);
        }
      );
  }

  // --- Configure mode: Generate series ---

  generateSeries() {
    if (!this.series) return;
    this.isSaving = true;
    this.editError = '';

    const c = this.editChanges;
    const patchData = this.buildPatchData(c);

    // Step 1: PATCH the series entity with configured values
    this._entityRESTService.patchEntity('standing_series', 'standing', this.series.id, patchData)
      .subscribe(
        () => {
          // Step 2: Generate with conflict check
          // Pass pre-computed instructors so the backend checks the correct
          // instructor per event (handles alternating rotation).
          const eventInstructors: { [date: string]: string } = {};
          this.livePreviewEvents.forEach(e => { if (e.instructorId) eventInstructors[e.date.substring(0, 10)] = e.instructorId; });
          const generateBody: any = {
            check_conflicts: true,
            event_instructors: eventInstructors,
            attendee_data: {
              field_enrollment: this.editChanges.enrollmentId || this.series!.enrollment_id || '',
              field_studio_reference: this._authService?.selectedStudio?.id || '',
            },
          };
          if (this.editChanges.endMode === 'enrollment' && this.series!.enrollment_lessons_available > 0) {
            generateBody.max_events = this.series!.enrollment_lessons_available;
          }
          this._drupalRESTService.httpPOST(
            `/api/standing-series/${this.series!.id}/generate`,
            generateBody
          ).subscribe(
            (genData: any) => {
              this.isSaving = false;
              if (genData?.has_conflicts) {
                this.conflicts = genData.conflicts;
                this.conflictDates = new Set(this.conflicts.map(c => c.date.substring(0, 10)));
                this.hasConflicts = true;
                this.updateLivePreview();
              } else if (genData?.success) {
                this._snackBar.open(
                  `Created ${genData.created_count || 0} appointments.`,
                  'OK',
                  { duration: 5000 }
                );
                this.finishAndClose({ action: 'generated', createdCount: genData.created_count || 0 });
              } else {
                this.editError = genData?.message || 'Failed to generate appointments.';
              }
            },
            error => {
              this.isSaving = false;
              this.editError = 'Failed to generate appointments.';
              LoggerService.error('Generate error:', error);
            }
          );
        },
        error => {
          this.isSaving = false;
          this.editError = 'Failed to update series configuration.';
          LoggerService.error('Patch error:', error);
        }
      );
  }

  generateIgnoringConflicts() {
    if (!this.series) return;
    this.isSaving = true;

    this._drupalRESTService.httpPOST(
      `/api/standing-series/${this.series.id}/generate`,
      {
        check_conflicts: false,
        attendee_data: {
          field_enrollment: this.editChanges.enrollmentId || this.series!.enrollment_id || '',
          field_studio_reference: this._authService?.selectedStudio?.id || '',
        },
      }
    ).subscribe(
      (data: any) => {
        this.isSaving = false;
        if (data?.success) {
          this._snackBar.open(
            `Created ${data.created_count || 0} appointments.`,
            'OK',
            { duration: 5000 }
          );
          this.finishAndClose({ action: 'generated', createdCount: data.created_count || 0 });
        } else {
          this.editError = data?.message || 'Failed to generate appointments.';
        }
      },
      error => {
        this.isSaving = false;
        this.editError = 'Failed to generate appointments.';
      }
    );
  }

  /** Build the PATCH data object for standing_series entity */
  private buildPatchData(c: typeof this.editChanges): any {
    const patchData: any = {
      field_days: (c.days || []).map(d => ({ value: d })),
      field_time: [{ value: c.time }],
      field_duration: [{ value: c.duration }],
      field_instructor: [{ target_id: c.instructor }],
      field_repetition_frequency: [{ value: c.repetitionFrequency }],
      field_repetition_units: [{ value: c.repetitionUnits }],
      field_end_mode: [{ value: c.endMode }],
      field_use_alternating_instructor: [{ value: (c.alternateInstructors && c.alternateInstructors.length > 0) ? 1 : 0 }],
    };

    if (c.lessonType) {
      patchData['field_type'] = [{ target_id: c.lessonType }];
    }

    if (c.notes !== undefined) {
      patchData['field_notes'] = c.notes ? [{ value: c.notes }] : [];
    }

    if (c.enrollmentId) {
      patchData['field_enrollment'] = [{ target_id: c.enrollmentId }];
    } else {
      patchData['field_enrollment'] = [];
    }

    if (c.endMode === 'date' && c.expirationDate) {
      patchData['field_expiration_date'] = [{ value: c.expirationDate + 'T23:59:59' }];
    } else if (c.endMode === 'indefinite') {
      patchData['field_expiration_date'] = [];
    }

    if (c.alternateInstructors && c.alternateInstructors.length > 0) {
      patchData['field_alternate_instructors'] = c.alternateInstructors.map(id => ({ target_id: id }));
    } else {
      patchData['field_alternate_instructors'] = [];
    }

    // Merge day-name overrides, date-specific overrides, and excluded dates into one JSON blob.
    const allOverrides: any = { ...(c.dayOverrides || {}), ...this.dateOverrides };
    for (const date of this.excludedDates) {
      const dateKey = date.substring(0, 10);
      allOverrides[dateKey] = { ...(allOverrides[dateKey] || {}), excluded: true };
    }
    if (Object.keys(allOverrides).length > 0) {
      patchData['field_day_overrides'] = [{ value: JSON.stringify(allOverrides) }];
    } else {
      patchData['field_day_overrides'] = [];
    }

    return patchData;
  }

  // --- Delete single event flow ---

  startDeleteSingle() {
    this.editError = '';
    this.showDeleteSingleConfirm = true;
  }

  cancelDeleteSingle() {
    this.showDeleteSingleConfirm = false;
    if (this.data.initialAction === 'deleteSingle') {
      this.closeWith(undefined);
    }
  }

  confirmDeleteSingle() {
    if (!this.series || !this.data.entityId) return;
    this.isDeletingSingle = true;

    this._drupalRESTService.httpPOST(
      `/api/standing-series/${this.series.id}/delete-future`,
      {
        scope: 'single',
        event_id: this.data.entityId,
        confirm: true
      }
    ).subscribe(
      (data: any) => {
        this.isDeletingSingle = false;
        if (data?.success) {
          this._snackBar.open('Event deleted.', 'OK', { duration: 3000 });
          this.closeWith({ action: 'deletedSingle' });
        } else {
          this.editError = data?.message || 'Failed to delete event.';
        }
      },
      error => {
        this.isDeletingSingle = false;
        this.editError = 'Failed to delete event.';
        LoggerService.error('Delete single error:', error);
      }
    );
  }

  // --- Deactivate flow ---

  startDeactivate() {
    this.editError = '';
    this.showDeactivateConfirm = true;
    this.deactivateDeleteFuture = true;
  }

  cancelDeactivate() {
    this.showDeactivateConfirm = false;
    if (this.data.initialAction === 'deleteSeries') {
      this.closeWith(undefined);
    }
  }

  confirmDeactivate() {
    if (!this.series) return;
    this.isDeactivating = true;

    this._drupalRESTService.httpPOST(
      `/api/standing-series/${this.series.id}/deactivate`,
      { delete_future: this.deactivateDeleteFuture }
    ).subscribe(
      (data: any) => {
        this.isDeactivating = false;
        if (data?.success) {
          const deleted = data.deleted_count ? ` and deleted ${data.deleted_count} future events` : '';
          this._snackBar.open(
            `Standing series deactivated${deleted}.`,
            'OK',
            { duration: 5000 }
          );
          this.closeWith({ action: 'deactivated', deletedCount: data.deleted_count || 0 });
        } else {
          this.editError = data?.message || 'Failed to delete the standing series.';
        }
      },
      error => {
        this.isDeactivating = false;
        this.editError = 'Failed to delete the standing series.';
        LoggerService.error('Deactivate error:', error);
      }
    );
  }

  close() {
    // In configure mode, deactivate the orphaned entity
    if (this.isConfigureMode && this.series) {
      this._drupalRESTService.httpPOST(
        `/api/standing-series/${this.series.id}/deactivate`,
        { delete_future: false }
      ).subscribe(() => {}, () => {});
    }
    this.closeWith(undefined);
  }

  /** Close dialog or emit to parent depending on mode */
  private closeWith(result: any) {
    if (!this.embedded && this.dialogRef) {
      this.dialogRef.close(result);
    } else {
      this.completed.emit(result);
    }
  }
}
